import { forwardRef, type ReactNode } from 'react';
import MuiTypography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { resolvePaletteColor, type ThemePaletteColor } from '../paletteColorPath';

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body'
  | 'bodyLight'
  | 'body2'
  | 'subtitle1'
  | 'subtitle2'
  | 'caption'
  | 'overline'
  | 'navLabel'
  | 'mobileNavLabel';

export type TypographyColor = ThemePaletteColor | 'inherit';

export type TypographyProps = {
  children?: ReactNode;
  variant: TypographyVariant;
  /** Theme palette path, e.g. `primary.main`, or `inherit`. */
  color?: TypographyColor;
  /** Truncates to a single line with an ellipsis instead of wrapping. */
  noWrap?: boolean;
};

const variantToMui: Record<
  TypographyVariant,
  'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'subtitle1' | 'subtitle2' | 'caption' | 'overline'
> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body: 'body1',
  bodyLight: 'body1',
  body2: 'body2',
  subtitle1: 'subtitle1',
  subtitle2: 'subtitle2',
  caption: 'caption',
  overline: 'overline',
  navLabel: 'caption',
  mobileNavLabel: 'caption'
};

/** Design-system typography; styles live in `Typography.theme.tsx`. */
export const Typography = forwardRef<HTMLElement, TypographyProps>(function Typography(
  { variant, color = 'inherit', noWrap, children },
  ref
) {
  const theme = useTheme();
  const resolvedColor = color === 'inherit' ? undefined : resolvePaletteColor(theme.palette, color);

  return (
    <MuiTypography
      ref={ref}
      variant={variantToMui[variant]}
      noWrap={noWrap}
      className={['SmTypography-root', `SmTypography-variant-${variant}`].join(' ')}
      sx={resolvedColor ? { color: resolvedColor } : undefined}
    >
      {children}
    </MuiTypography>
  );
});
